import { motion } from 'framer-motion'
import { Check, Zap, Briefcase, Building2 } from 'lucide-react'

interface PackagesSectionProps {
  onMVPClick: () => void
  onStandardClick: () => void
  onCorporateClick: () => void
}

const packages = [
  {
    icon: Zap,
    name: 'MVP',
    duration: '1-2 недели',
    price: 'от 45 000 ₽',
    features: [
      'Базовые сценарии и команды',
      'Простая админка',
      'Базовая аналитика',
      'Автоответы и FAQ',
      'Тех. поддержка 1 месяц - бесплатно'
    ],
    popular: false,
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    icon: Briefcase,
    name: 'Стандарт',
    duration: '3-4 недели',
    price: 'от 115 000 ₽',
    features: [
      'Все из MVP +',
      'Оплата в чате (ЮKassa/CloudPayments)',
      'Интеграция с CRM (Bitrix24, amoCRM)',
      'Согласования и воронки',
      'Расширенная отчётность',
      'Автоматические рассылки',
      'Тех. поддержка 3 месяца - бесплатно'
    ],
    popular: true,
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    icon: Building2,
    name: 'Корпоративный',
    duration: 'индивидуально',
    price: 'по запросу',
    features: [
      'Все из Стандарт +',
      'Расширенная безопасность',
      'Роли и права доступа',
      'Логирование и бэкапы',
      'Интеграция с 1С',
      'Кастомная отчётность',
      'Выделенный менеджер',
      'SLA и приоритетная поддержка'
    ],
    popular: false,
    gradient: 'from-orange-500 to-red-500'
  }
]

export default function PackagesSection({ onMVPClick, onStandardClick, onCorporateClick }: PackagesSectionProps) {
  const getClickHandler = (packageName: string) => {
    switch (packageName) {
      case 'MVP':
        return onMVPClick
      case 'Стандарт':
        return onStandardClick
      case 'Корпоративный':
        return onCorporateClick
      default:
        return onMVPClick
    }
  }
  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Пакеты и сроки
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            MVP-ориентированные решения для быстрого старта вашего проекта
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              className={`relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all ${
                pkg.popular ? 'ring-2 ring-purple-500' : 'border border-gray-200'
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Популярный
                  </span>
                </div>
              )}

              <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${pkg.gradient} flex items-center justify-center mb-6`}>
                <pkg.icon className="w-8 h-8 text-white" strokeWidth={2} />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {pkg.name}
              </h3>
              
              <p className="text-gray-600 mb-4">{pkg.duration}</p>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">{pkg.price}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={getClickHandler(pkg.name)}
                className={`w-full py-3 rounded-xl font-semibold transition-all ${
                  pkg.popular
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Выбрать пакет
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
